/**
 * Shared CON ↔ Achat invoice resolution (no React, no I/O).
 * Used by invoiceService / paymentService for stable, loop-safe consolidation.
 */

export function isConsolidatedInvoiceRecord(invoice) {
  return Boolean(
    invoice?.isConsolidated ||
      invoice?.type === 'consolidated' ||
      (invoice?.type && String(invoice.type).includes('consolidated'))
  );
}

/**
 * Index base (non-CON) invoices for O(1) lookup by id / purchaseId / originalTransactionId.
 */
export function indexBaseInvoices(invoices = []) {
  const byId = new Map();
  const byPurchaseId = new Map();
  for (const inv of invoices || []) {
    if (!inv || isConsolidatedInvoiceRecord(inv)) continue;
    if (inv.id != null && inv.id !== '') {
      byId.set(String(inv.id), inv);
    }
    const purchaseKey = inv.purchaseId ?? inv.originalTransactionId;
    if (purchaseKey != null && purchaseKey !== '') {
      const pk = String(purchaseKey);
      if (!byPurchaseId.has(pk)) byPurchaseId.set(pk, inv);
    }
  }
  return { byId, byPurchaseId };
}

/**
 * Resolve a CON.originalInvoices ref to the live Achat/Payment invoice.
 * Match order: invoice.id → purchaseId / originalTransactionId.
 * Does NOT match by invoiceNumber alone (avoids wrong merges after renumber).
 */
export function resolveLiveChildInvoice(ref, allInvoices = [], indexes = null) {
  if (!ref) return null;
  const { byId, byPurchaseId } = indexes || indexBaseInvoices(allInvoices);
  const refId = ref.id != null && ref.id !== '' ? String(ref.id) : '';
  const refPurchaseId =
    ref.purchaseId != null && ref.purchaseId !== '' ? String(ref.purchaseId) : '';

  if (refId) {
    if (byId.has(refId)) return byId.get(refId);
    if (byPurchaseId.has(refId)) return byPurchaseId.get(refId);
  }
  if (refPurchaseId) {
    if (byPurchaseId.has(refPurchaseId)) return byPurchaseId.get(refPurchaseId);
    if (byId.has(refPurchaseId)) return byId.get(refPurchaseId);
  }
  return null;
}

export function refMatchesInvoice(ref, invoice) {
  if (!ref || !invoice || isConsolidatedInvoiceRecord(invoice)) return false;
  const resolved = resolveLiveChildInvoice(ref, [invoice]);
  return Boolean(resolved && String(resolved.id) === String(invoice.id));
}

/** Live Achat id as shown on Achats page. */
export function getAchatDisplayId(invoiceOrRef) {
  if (!invoiceOrRef) return '';
  return String(
    invoiceOrRef.purchaseId ||
      invoiceOrRef.originalTransactionId ||
      invoiceOrRef.achatId ||
      ''
  );
}

/**
 * Semantic fingerprint — equal lists ⇒ safe to skip persist / return prev reference.
 */
export function invoicesFingerprint(invoices = []) {
  return (invoices || [])
    .map((i) => {
      if (!i) return '';
      const originals = (i.originalInvoices || [])
        .map(
          (o) =>
            `${o?.id || ''}:${o?.purchaseId || ''}:${o?.invoiceNumber || ''}:${o?.amount ?? ''}:${o?.paidAmount ?? 0}`
        )
        .join(',');
      const payLen = Array.isArray(i.payments) ? i.payments.length : 0;
      return [
        i.id,
        i.invoiceNumber,
        i.type,
        i.isConsolidated ? 1 : 0,
        i.totalAmount ?? 0,
        i.paidAmount ?? 0,
        i.status || '',
        i.lastPaymentDate || '',
        payLen,
        originals
      ].join('|');
    })
    .sort()
    .join(';');
}

export function areInvoicesEquivalent(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  return invoicesFingerprint(a) === invoicesFingerprint(b);
}
